import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Chip,
    Avatar,
    LinearProgress,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { Assessment, Recommend } from '@mui/icons-material';

const DetailedUserResults = ({ filteredData, performanceConfig }) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Helper function to get max score from assessment JSON or default to 60
    const getMaxScore = (assessment) => {
        if (assessment?.json) {
            try {
                const jsonData = typeof assessment.json === 'string' ? JSON.parse(assessment.json) : assessment.json;
                return jsonData.max_score || 60;
            } catch (error) {
                console.log('Error parsing JSON for max_score:', error);
                return 60;
            }
        }
        return assessment?.max_score || 60;
    };

    if (filteredData.length === 0) {
        return null;
    }

    const handleViewDetails = (user, assessment) => {
        setSelectedUser({...user, assessment: assessment});
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedUser(null);
    };

    return (
        <>
            <Box sx={{ px: 3, mb: 4 }}>
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    backgroundColor: 'white'
                }}
            >
                <Typography
                    variant="h6"
                    component="div"
                    sx={{
                        mb: 3,
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        color: '#0a0068',
                        fontFamily: 'Poppins'
                    }}
                >
                    Detailed User Performance
                </Typography>
                    {filteredData.map((assessment) => (
                        <Box key={assessment.id} sx={{ mb: 3 }}>
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5, fontSize: '0.9rem', color: '#cc4049' }}>
                                {assessment.assignment_name}
                            </Typography>
                            <Grid container spacing={1.5}>
                                {assessment.assessmentResults.map((user) => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={user.user_id}>
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                height: '100%',
                                                borderRadius: 2,
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                position: 'relative',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: 3
                                                }
                                            }}
                                        >
                                            <CardContent>
                                                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewDetails(user, assessment)}
                                                        sx={{
                                                            bgcolor: 'rgba(10, 0, 104, 0.05)',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(10, 0, 104, 0.1)',
                                                            }
                                                        }}
                                                    >
                                                        <VisibilityIcon fontSize="small" sx={{ color: '#0a0068' }} />
                                                    </IconButton>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, pr: 4 }}>
                                                    <Avatar
                                                        sx={{
                                                            mr: 2,
                                                            bgcolor: "#0a0068",
                                                            width: 40,
                                                            height: 40,
                                                            fontSize: '1.1rem'
                                                        }}
                                                    >
                                                        {user.json.user_details.fname?.charAt(0) || 'U'}
                                                    </Avatar>
                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                        <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ fontSize: '0.85rem' }}>
                                                            {user.json.user_details.fname} {user.json.user_details.lname}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
                                                            {user.json.user_details.email}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ mb: 2 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                        <Typography variant="h5" fontWeight="bold" color="#0a0068" sx={{ fontSize: '1.3rem' }}>
                                                            {user.json.finalScore}/{getMaxScore(assessment)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                            {Math.round((user.json.finalScore / getMaxScore(assessment)) * 100)}%
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={(user.json.finalScore / getMaxScore(assessment)) * 100}
                                                        sx={{
                                                            height: 8,
                                                            borderRadius: 4,
                                                            bgcolor: 'grey.200',
                                                            '& .MuiLinearProgress-bar': {
                                                                bgcolor: "#0a0068",
                                                                borderRadius: 4
                                                            }
                                                        }}
                                                    />
                                                </Box>

                                                <Chip
                                                    label={user.json.finalInterpretation.name}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: performanceConfig[user.json.finalInterpretation.name]?.bgColor || '#f5f5f5',
                                                        color: performanceConfig[user.json.finalInterpretation.name]?.textColor || '#666',
                                                        fontWeight: 600,
                                                        fontSize: '0.7rem',
                                                        height: 20,
                                                        mb: 1,
                                                        width: '100%'
                                                    }}
                                                />

                                                <Divider sx={{ my: 1 }} />
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                    <strong>Client:</strong> {user.json.user_details.user_batch}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.75rem' }}>
                                                    <strong>Experience Level:</strong> {user.json.user_details.experience_level || 'N/A'}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ))}
            </Paper>
        </Box>

            {/* Question Details Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: '#0a0068',
                    color: 'white'
                }}>
                    <Box>
                        <Typography variant="h6" component="div">
                            Assessment Summary
                        </Typography>
                        {selectedUser && (
                            <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                                {selectedUser.json.user_details.fname} {selectedUser.json.user_details.lname}
                            </Typography>
                        )}
                    </Box>
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={handleCloseDialog}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 3, maxHeight: '80vh', overflow: 'auto' }}>
                    {selectedUser && selectedUser.json.assessment_data && (
                        <>
                            {/* Summary Cards Section */}
                            <Box sx={{ mb: 4 }}>
                                {/* <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontWeight: 600 }}>
                                    Assessment Summary
                                </Typography> */}

                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    {/* Interpretation Card */}
                                    <Grid item xs={12} md={6}>
                                        <Card
                                            elevation={2}
                                            sx={{
                                                height: '100%',
                                                border: '1px solid #e3f2fd',
                                                transition: 'all 0.2s ease-in-out',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: 3
                                                }
                                            }}
                                        >
                                            <CardContent sx={{ p: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                    <Avatar sx={{ bgcolor: '#1976d2', mr: 2, width: 32, height: 32 }}>
                                                        <Assessment fontSize="small" />
                                                    </Avatar>
                                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', fontSize: '0.95rem' }}>
                                                        Interpretation
                                                    </Typography>
                                                </Box>
                                                <Divider sx={{ mb: 2 }} />
                                                <div
                                                    style={{
                                                        fontSize: "14px",
                                                        lineHeight: "1.6",
                                                        color: '#424242'
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: selectedUser.json.finalInterpretation?.description || "No interpretation available"
                                                    }}
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {/* Recommendation Card */}
                                    <Grid item xs={12} md={6}>
                                        <Card
                                            elevation={2}
                                            sx={{
                                                height: '100%',
                                                border: '1px solid #f3e5f5',
                                                transition: 'all 0.2s ease-in-out',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: 3
                                                }
                                            }}
                                        >
                                            <CardContent sx={{ p: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                    <Avatar sx={{ bgcolor: '#7b1fa2', mr: 2, width: 32, height: 32 }}>
                                                        <Recommend fontSize="small" />
                                                    </Avatar>
                                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#7b1fa2', fontSize: '0.95rem' }}>
                                                        Recommendation
                                                    </Typography>
                                                </Box>
                                                <Divider sx={{ mb: 2 }} />
                                                <div
                                                    style={{
                                                        fontSize: "14px",
                                                        lineHeight: "1.6",
                                                        color: '#424242'
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: selectedUser.json.finalRecommendation?.description || "No recommendation available"
                                                    }}
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Assessment Details Section */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600, fontSize: '1rem' }}>
                                        Assessment Details
                                    </Typography>
                                    <Chip
                                        label={`${selectedUser.json.assessment_data.length} Questions`}
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                    />
                                </Box>

                                <Card elevation={1} sx={{ border: '1px solid #e0e0e0' }}>
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#fafafa' }}>
                                                    <TableCell
                                                        sx={{
                                                            fontWeight: 700,
                                                            width: 80,
                                                            color: '#424242',
                                                            borderBottom: '2px solid #e0e0e0'
                                                        }}
                                                    >
                                                        #
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: '#424242',
                                                            borderBottom: '2px solid #e0e0e0'
                                                        }}
                                                    >
                                                        Question
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: '#424242',
                                                            borderBottom: '2px solid #e0e0e0'
                                                        }}
                                                    >
                                                        Selected Answer
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            fontWeight: 700,
                                                            width: 120,
                                                            color: '#424242',
                                                            borderBottom: '2px solid #e0e0e0'
                                                        }}
                                                    >
                                                        Score
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {selectedUser.json.assessment_data.map((item, index) => (
                                                    <TableRow
                                                        key={index}
                                                        sx={{
                                                            '&:nth-of-type(even)': {
                                                                bgcolor: '#fafafa'
                                                            },
                                                            '&:hover': {
                                                                bgcolor: '#f5f5f5',
                                                                transition: 'background-color 0.2s ease'
                                                            },
                                                            borderBottom: '1px solid #e0e0e0'
                                                        }}
                                                    >
                                                        <TableCell sx={{
                                                            fontSize: '0.9rem',
                                                            fontWeight: 600,
                                                            color: '#666',
                                                            py: 2
                                                        }}>
                                                            {String(index + 1).padStart(2, '0')}
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            fontSize: '0.9rem',
                                                            color: '#424242',
                                                            py: 2,
                                                            lineHeight: 1.5
                                                        }}>
                                                            {item.question}
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            fontSize: '0.9rem',
                                                            color: '#424242',
                                                            py: 2,
                                                            lineHeight: 1.5
                                                        }}>
                                                            <Box sx={{
                                                                bgcolor: '#f8f9fa',
                                                                p: 1.5,
                                                                borderRadius: 1,
                                                                border: '1px solid #e9ecef'
                                                            }}>
                                                                {item.answer.replace(/ \(Score: \d+\)/, '')}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ py: 2 }}>
                                                            <Chip
                                                                label={item.score}
                                                                size="medium"
                                                                sx={{
                                                                    bgcolor: item.score >= 3 ? '#4caf50' :
                                                                        item.score >= 2 ? '#ff9800' : '#f44336',
                                                                    color: 'white',
                                                                    fontWeight: 700,
                                                                    minWidth: 45,
                                                                    '&:hover': {
                                                                        transform: 'scale(1.05)',
                                                                        transition: 'transform 0.2s ease'
                                                                    }
                                                                }}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Card>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Total Score: <strong>{selectedUser?.json.finalScore || 0}/{selectedUser ? getMaxScore(selectedUser.assessment) : 60}</strong>
                        </Typography>
                        <Button onClick={handleCloseDialog} variant="contained" sx={{ bgcolor: '#0a0068' }}>
                            Close
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DetailedUserResults;